// Interface language (phase 9): English, Latvian, Estonian, Lithuanian.
//
// gettext style: write `t("English text")`; dictionaries map the English text to a translation,
// so anything not translated yet shows in English instead of a key. `{name}` placeholders are
// filled from the second argument. Numbers, money and dates follow the language's locale
// (format.ts). Translations grow page by page — add the English string to the three dictionaries.

export type Locale = "en" | "lv" | "et" | "lt";
export const LOCALES: { value: Locale; label: string; tag: string }[] = [
  { value: "en", label: "English", tag: "en-IE" },
  { value: "lv", label: "Latviešu", tag: "lv-LV" },
  { value: "et", label: "Eesti", tag: "et-EE" },
  { value: "lt", label: "Lietuvių", tag: "lt-LT" },
];

const stored = (() => {
  try {
    return localStorage.getItem("keel-locale");
  } catch {
    return null;
  }
})();
export const locale: Locale = LOCALES.some((l) => l.value === stored) ? (stored as Locale) : "en";
export const localeTag = LOCALES.find((l) => l.value === locale)!.tag;

/** Remember the language and reload, so every formatter and label picks it up. */
export function setLocale(l: Locale) {
  try {
    localStorage.setItem("keel-locale", l);
  } catch {
    /* private mode */
  }
  if (l !== locale) location.reload();
}

type Dict = Record<string, string>;

const lv: Dict = {
  Overview: "Pārskats",
  Sales: "Pārdošana",
  Purchasing: "Iepirkumi",
  Inventory: "Krājumi",
  Accounting: "Grāmatvedība",
  Platform: "Platforma",
  People: "Personāls",
  Manufacturing: "Ražošana",
  "Fixed assets": "Pamatlīdzekļi",
  Loans: "Aizdevumi",
  Warehouse: "Noliktava",
  Putaway: "Novietošana",
  "Stock counts": "Inventarizācijas",
  "Zones & bins": "Zonas un vietas",
  Menu: "Izvēlne",
  Sections: "Sadaļas",
  "Legal entities": "Juridiskās personas",
  "Other accounts": "Citi konti",
  "Pin the page list": "Piespraust lapu sarakstu",
  "Unpin the page list": "Atspraust lapu sarakstu",
  Operations: "Ražošana",
  Finance: "Finanses",
  Orders: "Pasūtījumi",
  Deliveries: "Piegādes",
  Customers: "Klienti",
  Invoices: "Rēķini",
  "Purchase orders": "Iepirkuma pasūtījumi",
  Suppliers: "Piegādātāji",
  Bills: "Ienākošie rēķini",
  Stock: "Atlikumi",
  Movements: "Kustības",
  Products: "Produkti",
  Warehouses: "Noliktavas",
  "Sales ledger": "Debitori",
  "Purchase ledger": "Kreditori",
  VAT: "PVN",
  Payments: "Maksājumi",
  "General ledger": "Virsgrāmata",
  Journal: "Žurnāls",
  "Trial balance": "Apgrozījuma pārskats",
  "P&L and balance sheet": "Peļņa un bilance",
  "Financial statements": "Finanšu pārskati",
  "Cash flow": "Naudas plūsma",
  "Accruals & deferrals": "Uzkrājumi un nākamo periodu ieņēmumi",
  "Recurring entries": "Periodiskie grāmatojumi",
  Budgets: "Budžeti",
  "Annual report": "Gada pārskats",
  "Chart of accounts": "Kontu plāns",
  "Posting groups": "Grāmatošanas grupas",
  Periods: "Periodi",
  "Integration hub": "Integrācijas",
  Webhooks: "Webhooks",
  "API keys": "API atslēgas",
  "Audit log": "Audita žurnāls",
  Settings: "Iestatījumi",
  "Customers & plans": "Klienti un plāni",
  "My account": "Mans konts",
  "Search or jump to…": "Meklēt vai pāriet uz…",
  New: "Jauns",
  "Sign out": "Iziet",
  Help: "Palīdzība",
  "What's new": "Jaunumi",
  "Help for this page": "Palīdzība par šo lapu",
  "What's new in Keel": "Jaunumi Keel",
  "Got it": "Skaidrs",
  "All release notes": "Visas izmaiņu piezīmes",
  "All help articles": "Visi palīdzības raksti",
  "Open in the knowledge base": "Atvērt zināšanu bāzē",
  "Search help…": "Meklēt palīdzībā…",
  "What is this?": "Kas tas ir?",
  "How this works": "Kā tas darbojas",
  "Light mode": "Gaišais režīms",
  "Dark mode": "Tumšais režīms",
  "Sign in": "Pieteikties",
  "Sales, stock, finance and every system they talk to.":
    "Pārdošana, krājumi, finanses un visas sistēmas, ar kurām tās sarunājas.",
  Email: "E-pasts",
  Password: "Parole",
  "Signing in…": "Piesakās…",
  "Forgot password?": "Aizmirsāt paroli?",
  "Back to sign in": "Atpakaļ uz pieteikšanos",
  "Send reset link": "Sūtīt atjaunošanas saiti",
  "Sign in with SSO": "Pieteikties ar SSO",
  "Authenticator code": "Autentifikatora kods",
  Account: "Konts",
  "Continue to your identity provider": "Turpināt pie identitātes nodrošinātāja",
  "If that address has an account, a link to choose a new password is on its way.":
    "Ja šai adresei ir konts, saite jaunas paroles izvēlei jau ir ceļā.",
  "Choose a password": "Izvēlieties paroli",
  "New password": "Jaunā parole",
  "Repeat it": "Atkārtojiet to",
  "Set password": "Iestatīt paroli",
  "The passwords don't match": "Paroles nesakrīt",
  "Your password is set. Sign in with it now.": "Parole ir iestatīta. Piesakieties ar to.",
  "Go to sign in": "Uz pieteikšanos",
  "Change password": "Mainīt paroli",
  "Current password": "Pašreizējā parole",
  "Two-factor sign-in": "Divfaktoru pieteikšanās",
  "Set up": "Iestatīt",
  "Turn on": "Ieslēgt",
  "Turn off": "Izslēgt",
  "Code from the app": "Kods no lietotnes",
  "Where you're signed in": "Kur esat pieteicies",
  "Sign out everywhere": "Iziet visur",
  Device: "Ierīce",
  Address: "Adrese",
  "Signed in": "Pieteicies",
  "Last active": "Pēdējā aktivitāte",
  "this browser": "šī pārlūkprogramma",
  Invitations: "Uzaicinājumi",
  Accept: "Pieņemt",
  Decline: "Noraidīt",
  Language: "Valoda",
  Cancel: "Atcelt",
  "Keep editing": "Turpināt rediģēt",
  Discard: "Atmest",
  "Discard what you've entered?": "Atmest ievadīto?",
  Save: "Saglabāt",
  "Export CSV": "Eksportēt CSV",
  Print: "Drukāt",
  "Export data": "Eksportēt datus",
  // ERP-162: pages a role cannot use.
  "No access": "Nav piekļuves",
  "Check again": "Pārbaudīt vēlreiz",
  "Go to Overview": "Uz pārskatu",
  Everything: "Viss",
  all: "viss",
  "This page needs the permission {perm}, which your role doesn't have. Ask an administrator to add it.":
    "Šai lapai vajag tiesības {perm}, kuru jūsu lomai nav. Lūdziet administratoram tās pievienot.",
  "Your role doesn't cover this page. Ask an administrator to add it.":
    "Jūsu loma neaptver šo lapu. Lūdziet administratoram to pievienot.",
  "Part of this page is hidden: your role doesn't have {perms}.":
    "Daļa lapas ir paslēpta: jūsu lomai nav {perms}.",
};

const et: Dict = {
  Overview: "Ülevaade",
  Sales: "Müük",
  Purchasing: "Ost",
  Inventory: "Ladu",
  Accounting: "Raamatupidamine",
  Platform: "Platvorm",
  People: "Personal",
  Manufacturing: "Tootmine",
  "Fixed assets": "Põhivara",
  Loans: "Laenud",
  Warehouse: "Ladu",
  Putaway: "Paigutamine",
  "Stock counts": "Inventuurid",
  "Zones & bins": "Tsoonid ja kohad",
  Menu: "Menüü",
  Sections: "Jaotised",
  "Legal entities": "Juriidilised isikud",
  "Other accounts": "Muud kontod",
  "Pin the page list": "Kinnita lehtede loend",
  "Unpin the page list": "Vabasta lehtede loend",
  Operations: "Tootmine",
  Finance: "Finants",
  Orders: "Tellimused",
  Deliveries: "Tarned",
  Customers: "Kliendid",
  Invoices: "Arved",
  "Purchase orders": "Ostutellimused",
  Suppliers: "Tarnijad",
  Bills: "Ostuarved",
  Stock: "Laoseis",
  Movements: "Liikumised",
  Products: "Tooted",
  Warehouses: "Laod",
  "Sales ledger": "Müügireskontro",
  "Purchase ledger": "Ostureskontro",
  VAT: "KM",
  Payments: "Maksed",
  "General ledger": "Pearaamat",
  Journal: "Päevaraamat",
  "Trial balance": "Proovibilanss",
  "P&L and balance sheet": "Kasumiaruanne ja bilanss",
  "Financial statements": "Finantsaruanded",
  "Cash flow": "Rahavoog",
  "Accruals & deferrals": "Viitvõlad ja ettemaksed",
  "Recurring entries": "Korduvad kanded",
  Budgets: "Eelarved",
  "Annual report": "Majandusaasta aruanne",
  "Chart of accounts": "Kontoplaan",
  "Posting groups": "Konteerimisgrupid",
  Periods: "Perioodid",
  "Integration hub": "Integratsioonid",
  Webhooks: "Webhookid",
  "API keys": "API võtmed",
  "Audit log": "Auditilogi",
  Settings: "Seaded",
  "Customers & plans": "Kliendid ja paketid",
  "My account": "Minu konto",
  "Search or jump to…": "Otsi või liigu…",
  New: "Uus",
  "Sign out": "Logi välja",
  Help: "Abi",
  "What's new": "Mis on uut",
  "Help for this page": "Selle lehe abi",
  "What's new in Keel": "Mis on Keelis uut",
  "Got it": "Selge",
  "All release notes": "Kõik väljalaskemärkmed",
  "All help articles": "Kõik abiartiklid",
  "Open in the knowledge base": "Ava teadmusbaasis",
  "Search help…": "Otsi abist…",
  "What is this?": "Mis see on?",
  "How this works": "Kuidas see töötab",
  "Light mode": "Hele režiim",
  "Dark mode": "Tume režiim",
  "Sign in": "Logi sisse",
  "Sales, stock, finance and every system they talk to.":
    "Müük, ladu, finants ja kõik süsteemid, millega need suhtlevad.",
  Email: "E-post",
  Password: "Parool",
  "Signing in…": "Sisselogimine…",
  "Forgot password?": "Unustasid parooli?",
  "Back to sign in": "Tagasi sisselogimisse",
  "Send reset link": "Saada lähtestamise link",
  "Sign in with SSO": "Logi sisse SSO-ga",
  "Authenticator code": "Autentimisrakenduse kood",
  Account: "Konto",
  "Continue to your identity provider": "Jätka identiteedipakkuja juurde",
  "If that address has an account, a link to choose a new password is on its way.":
    "Kui sellel aadressil on konto, on uue parooli valimise link teel.",
  "Choose a password": "Vali parool",
  "New password": "Uus parool",
  "Repeat it": "Korda seda",
  "Set password": "Määra parool",
  "The passwords don't match": "Paroolid ei kattu",
  "Your password is set. Sign in with it now.": "Parool on määratud. Logi sellega sisse.",
  "Go to sign in": "Sisselogimisse",
  "Change password": "Muuda parooli",
  "Current password": "Praegune parool",
  "Two-factor sign-in": "Kaheastmeline sisselogimine",
  "Set up": "Seadista",
  "Turn on": "Lülita sisse",
  "Turn off": "Lülita välja",
  "Code from the app": "Kood rakendusest",
  "Where you're signed in": "Kus oled sisse logitud",
  "Sign out everywhere": "Logi kõikjal välja",
  Device: "Seade",
  Address: "Aadress",
  "Signed in": "Sisse logitud",
  "Last active": "Viimati aktiivne",
  "this browser": "see brauser",
  Invitations: "Kutsed",
  Accept: "Nõustu",
  Decline: "Keeldu",
  Language: "Keel",
  Cancel: "Tühista",
  "Keep editing": "Jätka muutmist",
  Discard: "Loobu",
  "Discard what you've entered?": "Loobuda sisestatust?",
  Save: "Salvesta",
  "Export CSV": "Ekspordi CSV",
  Print: "Prindi",
  "Export data": "Ekspordi andmed",
  // ERP-162: pages a role cannot use.
  "No access": "Juurdepääs puudub",
  "Check again": "Kontrolli uuesti",
  "Go to Overview": "Ülevaatesse",
  Everything: "Kõik",
  all: "kõik",
  "This page needs the permission {perm}, which your role doesn't have. Ask an administrator to add it.":
    "See leht vajab õigust {perm}, mida teie rollil pole. Paluge administraatoril see lisada.",
  "Your role doesn't cover this page. Ask an administrator to add it.":
    "Teie roll ei hõlma seda lehte. Paluge administraatoril see lisada.",
  "Part of this page is hidden: your role doesn't have {perms}.":
    "Osa lehest on peidetud: teie rollil pole {perms}.",
};

const lt: Dict = {
  Overview: "Apžvalga",
  Sales: "Pardavimai",
  Purchasing: "Pirkimai",
  Inventory: "Atsargos",
  Accounting: "Apskaita",
  Platform: "Platforma",
  People: "Personalas",
  Manufacturing: "Gamyba",
  "Fixed assets": "Ilgalaikis turtas",
  Loans: "Paskolos",
  Warehouse: "Sandėlis",
  Putaway: "Sudėjimas",
  "Stock counts": "Inventorizacijos",
  "Zones & bins": "Zonos ir vietos",
  Menu: "Meniu",
  Sections: "Skiltys",
  "Legal entities": "Juridiniai asmenys",
  "Other accounts": "Kitos paskyros",
  "Pin the page list": "Prisegti puslapių sąrašą",
  "Unpin the page list": "Atsegti puslapių sąrašą",
  Operations: "Gamyba",
  Finance: "Finansai",
  Orders: "Užsakymai",
  Deliveries: "Pristatymai",
  Customers: "Klientai",
  Invoices: "Sąskaitos",
  "Purchase orders": "Pirkimo užsakymai",
  Suppliers: "Tiekėjai",
  Bills: "Gautos sąskaitos",
  Stock: "Likučiai",
  Movements: "Judėjimai",
  Products: "Produktai",
  Warehouses: "Sandėliai",
  "Sales ledger": "Debitoriai",
  "Purchase ledger": "Kreditoriai",
  VAT: "PVM",
  Payments: "Mokėjimai",
  "General ledger": "Didžioji knyga",
  Journal: "Žurnalas",
  "Trial balance": "Bandomasis balansas",
  "P&L and balance sheet": "Pelnas ir balansas",
  "Financial statements": "Finansinės ataskaitos",
  "Cash flow": "Pinigų srautai",
  "Accruals & deferrals": "Sukauptos sąnaudos ir ateinančių laikotarpių pajamos",
  "Recurring entries": "Pasikartojantys įrašai",
  Budgets: "Biudžetai",
  "Annual report": "Metinė ataskaita",
  "Chart of accounts": "Sąskaitų planas",
  "Posting groups": "Registravimo grupės",
  Periods: "Laikotarpiai",
  "Integration hub": "Integracijos",
  Webhooks: "Webhook'ai",
  "API keys": "API raktai",
  "Audit log": "Audito žurnalas",
  Settings: "Nustatymai",
  "Customers & plans": "Klientai ir planai",
  "My account": "Mano paskyra",
  "Search or jump to…": "Ieškoti arba pereiti į…",
  New: "Naujas",
  "Sign out": "Atsijungti",
  Help: "Pagalba",
  "What's new": "Naujienos",
  "Help for this page": "Šio puslapio pagalba",
  "What's new in Keel": "Keel naujienos",
  "Got it": "Supratau",
  "All release notes": "Visos versijų pastabos",
  "All help articles": "Visi pagalbos straipsniai",
  "Open in the knowledge base": "Atidaryti žinių bazėje",
  "Search help…": "Ieškoti pagalboje…",
  "What is this?": "Kas tai?",
  "How this works": "Kaip tai veikia",
  "Light mode": "Šviesus režimas",
  "Dark mode": "Tamsus režimas",
  "Sign in": "Prisijungti",
  "Sales, stock, finance and every system they talk to.":
    "Pardavimai, atsargos, finansai ir visos sistemos, su kuriomis jie bendrauja.",
  Email: "El. paštas",
  Password: "Slaptažodis",
  "Signing in…": "Jungiamasi…",
  "Forgot password?": "Pamiršote slaptažodį?",
  "Back to sign in": "Atgal į prisijungimą",
  "Send reset link": "Siųsti atkūrimo nuorodą",
  "Sign in with SSO": "Prisijungti per SSO",
  "Authenticator code": "Autentifikatoriaus kodas",
  Account: "Paskyra",
  "Continue to your identity provider": "Tęsti pas tapatybės teikėją",
  "If that address has an account, a link to choose a new password is on its way.":
    "Jei šiuo adresu yra paskyra, nuoroda naujam slaptažodžiui jau siunčiama.",
  "Choose a password": "Pasirinkite slaptažodį",
  "New password": "Naujas slaptažodis",
  "Repeat it": "Pakartokite",
  "Set password": "Nustatyti slaptažodį",
  "The passwords don't match": "Slaptažodžiai nesutampa",
  "Your password is set. Sign in with it now.": "Slaptažodis nustatytas. Prisijunkite su juo.",
  "Go to sign in": "Į prisijungimą",
  "Change password": "Keisti slaptažodį",
  "Current password": "Dabartinis slaptažodis",
  "Two-factor sign-in": "Dviejų veiksnių prisijungimas",
  "Set up": "Nustatyti",
  "Turn on": "Įjungti",
  "Turn off": "Išjungti",
  "Code from the app": "Kodas iš programėlės",
  "Where you're signed in": "Kur esate prisijungę",
  "Sign out everywhere": "Atsijungti visur",
  Device: "Įrenginys",
  Address: "Adresas",
  "Signed in": "Prisijungta",
  "Last active": "Paskutinis aktyvumas",
  "this browser": "ši naršyklė",
  Invitations: "Kvietimai",
  Accept: "Priimti",
  Decline: "Atmesti",
  Language: "Kalba",
  Cancel: "Atšaukti",
  "Keep editing": "Tęsti redagavimą",
  Discard: "Atmesti",
  "Discard what you've entered?": "Atmesti, ką įvedėte?",
  Save: "Išsaugoti",
  "Export CSV": "Eksportuoti CSV",
  Print: "Spausdinti",
  "Export data": "Eksportuoti duomenis",
  // ERP-162: pages a role cannot use.
  "No access": "Nėra prieigos",
  "Check again": "Tikrinti dar kartą",
  "Go to Overview": "Į apžvalgą",
  Everything: "Viskas",
  all: "visi",
  "This page needs the permission {perm}, which your role doesn't have. Ask an administrator to add it.":
    "Šiam puslapiui reikia teisės {perm}, kurios jūsų vaidmuo neturi. Paprašykite administratoriaus ją pridėti.",
  "Your role doesn't cover this page. Ask an administrator to add it.":
    "Jūsų vaidmuo neapima šio puslapio. Paprašykite administratoriaus jį pridėti.",
  "Part of this page is hidden: your role doesn't have {perms}.":
    "Dalis puslapio paslėpta: jūsų vaidmuo neturi {perms}.",
};

const DICTS: Record<Locale, Dict> = { en: {}, lv, et, lt };

/** Translate English UI text; untranslated text stays English. */
export function t(text: string, vars?: Record<string, string | number>): string {
  let s = DICTS[locale][text] ?? text;
  if (vars) for (const [k, v] of Object.entries(vars)) s = s.replaceAll(`{${k}}`, String(v));
  return s;
}

/** For tests and tooling: which English strings a language still lacks. */
export const missing = (l: Locale, texts: string[]) => texts.filter((x) => !(x in DICTS[l]));
